import React, { useEffect, useImperativeHandle, useRef } from "react";

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise = null;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () =>
      window.turnstile
        ? resolve(window.turnstile)
        : reject(new Error("Turnstile unavailable"));
    script.onerror = () => {
      scriptPromise = null;
      script.remove();
      reject(new Error("Could not load the security check."));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Explicitly rendered Cloudflare Turnstile. The widget adds the hidden
 * `cf-turnstile-response` input to its parent form; `onToken` also mirrors the token.
 * `ref.current.reset()` fetches a fresh token (tokens are single-use).
 */
const TurnstileWidget = ({ siteKey, onToken, onError, ref }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  onTokenRef.current = onToken;
  onErrorRef.current = onError;

  useImperativeHandle(ref, () => ({
    reset() {
      onTokenRef.current?.("");
      if (window.turnstile && widgetIdRef.current != null) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: "event-registration",
          theme: "light",
          size: "flexible",
          callback: (token) => onTokenRef.current?.(token),
          "expired-callback": () => onTokenRef.current?.(""),
          "error-callback": () => {
            onTokenRef.current?.("");
            onErrorRef.current?.("The security check failed to load. Please retry.");
          },
        });
      })
      .catch((err) => {
        if (!cancelled) onErrorRef.current?.(err.message);
      });
    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current != null) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [siteKey]);

  return <div ref={containerRef} className="smym-turnstile" />;
};

export default TurnstileWidget;
